import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { ApiEndpoints, PageNames, Routes } from '../../../../utils/app-constants';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageBody } from '../../../../components/page-components/PageBody';
import { Table } from '../../../../components/tables/Table';
import { navigateToPage } from '../../../../utils/history';
import { InventorySvc } from '../../../../shared/services/inventory-service';
import { PhysicalHorizontalEntry, PhysicalHorizontalEntryPart } from '../../../../modules/inventory/models/physical-control-group-entry';
import { StatusCode } from '../../../../utils/api';
import { InventoryEntryUpdate } from '../../../../modules/inventory/models/physical-inventory-entry-update';
import { AutoComplete } from '../../../../containers/autocomplete/AutoComplete';
import { useDispatch } from 'react-redux';
import { InventoryPartAutocompleteItem } from '../../../../modules/inventory/models/inventory-part-autocomplete-item';
import { AutoCompleteItem } from '../../../../models/utilities/autocomplete-item';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { PhysicalControlGroupStatus } from '../../../../modules/inventory/models/physical-inventory-details';
import { PhysicalInventoryStatus } from '../../../../modules/inventory/models/physical-inventory-enums';
import { getQuantity } from '../../../../modules/inventory/models/utils';

interface IProps {
    location: any;
}

export const InventoryPhysicalHorizontalEntryEditScene = React.forwardRef((props: IProps, ref) => {

    const dispatch = useDispatch();
    const [service] = React.useState(new InventorySvc());
    const [entry, setEntry] = React.useState(new PhysicalHorizontalEntry());

    const [newPart, setNewPart] = React.useState<InventoryPartAutocompleteItem|null>(null);

    function goBack() {
        var reportId = getReportId();
        navigateToPage(Routes.INVENTORY_PHYSICAL_DETAILS_CONTROLGROUPS.formattify(reportId));
    }

    async function getEntry(reportId: string, controlNumber: string, entryNumber: number) {

        if (controlNumber) {
            const result = await service.getControlGroupEntry(reportId, controlNumber, entryNumber);
            if (result.status === StatusCode.Success) {
                var entry = new PhysicalHorizontalEntry(result.data);
                setEntry(entry);
            }
        }
        else {
            goBack();
        }
    }

    function save(complete: boolean) {

        var reportId = getReportId();

        var payload = {
            reportId: reportId,
            controlNumber: entry.controlNumber,
            entryNumber: entry.entry,
            name: entry.name,
            serviceCenter: entry.serviceCenter,
            complete: complete,
            parts: entry.parts.map(p => {
                return {
                    partNumber: p.partNumber,
                    quantities: p.getQuantities()
                };
            })
        } as InventoryEntryUpdate;

        service.updateControlGroupEntry(reportId, payload)
            .then((result) => {
                if (result.status === StatusCode.Success) {
                    getEntry(getReportId(), getControlNumber(), Number.parseInt(getEntryNumber(), 10));
                }
            });
    }

    function disableEdit(): boolean{
        return entry.status == PhysicalControlGroupStatus.complete || entry.reportStatus == PhysicalInventoryStatus.complete
    }

    const getTopButtons = () => {

        return <HasPermission requiredPermissions={[
            Permissions.InventoryPhysicalControlGroupsEdit
        ]}>
            <button className="btn attention" onClick={() => save(false)} disabled={ disableEdit() }>
                SAVE
            </button>
        </HasPermission>;
    }

    const getBottomButtons = () => {

        return <HasPermission requiredPermissions={[
            Permissions.InventoryPhysicalControlGroupsEdit
        ]}>
            <button className="btn complete" onClick={() => save(true)} disabled={disableEdit()}>
                COMPLETE
            </button>
        </HasPermission>;
    }

    function getReportId() {
        const opts = props.location.pathname.split('/');
        return opts.length > 4 ? opts[4] : null;
    }

    function getControlNumber() {
        const opts = props.location.pathname.split('/');
        return opts.length > 8 ? opts[8] : null;
    }

    function getEntryNumber() {
        const opts = props.location.pathname.split('/');
        return opts.length > 9 ? opts[9] : null;
    }

    function addItem() {
        if (newPart) {
            var tmpEntry = { ...entry };

            var part = new PhysicalHorizontalEntryPart();
            part.partNumber = newPart.partNumber;
            part.type = newPart.type;
            part.subtype = newPart.subtype;
            part.unitOfMeasure = newPart.unitOfMeasure;
            part.description = newPart.description;
            part.isNew = true;

            tmpEntry.parts.push(part);
            setEntry(tmpEntry);
            setNewPart(null);
        }
    }

    function removeItem(idx: number) {
        var tmpEntry = { ...entry };
        tmpEntry.parts.splice(idx, 1);
        setEntry(tmpEntry);
    }


    function onPartSelected(e: AutoCompleteItem) {
        if (e?.extraData?.id) {
            setNewPart(e.extraData as InventoryPartAutocompleteItem)
        }
    }

    function handleBlur(e: React.ChangeEvent<HTMLInputElement>, idx: number) {
        var tmpEntry = { ...entry };
        // convert to number
            if (e.target.name == "quantity1") {
                tmpEntry.parts[idx].quantity1 = getQuantity(tmpEntry.parts[idx].quantity1);
            }
            if (e.target.name == "quantity2") {
                tmpEntry.parts[idx].quantity2 = getQuantity(tmpEntry.parts[idx].quantity2);
            }
            if (e.target.name == "quantity3") {
                tmpEntry.parts[idx].quantity3 = getQuantity(tmpEntry.parts[idx].quantity3);
            }
            if (e.target.name == "quantity4") {
                tmpEntry.parts[idx].quantity4 = getQuantity(tmpEntry.parts[idx].quantity4);
            }
            if (e.target.name == "quantity5") {
                tmpEntry.parts[idx].quantity5 = getQuantity(tmpEntry.parts[idx].quantity5);
            }
            if (e.target.name == "quantity6") {
                tmpEntry.parts[idx].quantity6 = getQuantity(tmpEntry.parts[idx].quantity6);
            }
        setEntry(tmpEntry);
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>, idx:number) {
        var tmpEntry = { ...entry };

        if (e?.target) {
            if (e.target.name == "quantity1") {
                tmpEntry.parts[idx].quantity1 = e.target.value;
            }
            if (e.target.name == "quantity2") {
                tmpEntry.parts[idx].quantity2 = e.target.value;
            }
            if (e.target.name == "quantity3") {
                tmpEntry.parts[idx].quantity3 = e.target.value;
            }
            if (e.target.name == "quantity4") {
                tmpEntry.parts[idx].quantity4 = e.target.value;
            }
            if (e.target.name == "quantity5") {
                tmpEntry.parts[idx].quantity5 = e.target.value;
            }
            if (e.target.name == "quantity6") {
                tmpEntry.parts[idx].quantity6 = e.target.value;
            }
        }

        setEntry(tmpEntry);
    }

    React.useEffect(() => {
        getEntry(getReportId(), getControlNumber(), Number.parseInt(getEntryNumber(), 10));
    }, [props.location]);

    return <PageContent pageName={PageNames.INVENTORY_PHYSICAL_CONTROL_GROUP_ENTRY} classes='inventory-physical'>
        <PageBody>
            <div className="row">
                <div className="col">
                    {getTopButtons()}
                </div>
            </div>
            <div className="row">
                <div className="col-2">
                    <span className="invnetory-label">Control ID</span>
                    <span className="invnetory-value">{entry.controlNumber}</span>
                </div>
                <div className="col-2">
                    <span className="invnetory-label">Entry</span>
                    <span className="invnetory-value">{entry.entry}</span>
                </div>
                <div className="col-5">
                    <span className="invnetory-label">Control Name</span>
                    <span className="invnetory-value"> {entry.name} </span>
                </div>
                <div className="col-3">
                    <span className="invnetory-label">Service Center</span>
                    <span className="invnetory-value"> {entry.serviceCenter} </span>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <Table
                        header={[
                            'TYPE',
                            'SUBTYPE',
                            'UNIT',
                            'DESCRIPTION',
                            'PART #',
                            'QTY 1',
                            'QTY 2',
                            'QTY 3',
                            'QTY 4',
                            'QTY 5',
                            'QTY 6'
                        ]}
                        noResultsMessage="No control groups in the report">
                        {
                            entry.parts.map((itm: PhysicalHorizontalEntryPart, idx: number) => {
                                return <tr>
                                    <td style={{ width: '10%' }}>
                                        {itm.isNew ? <button
                                            id="remove-item"
                                            onClick={(e) => removeItem(idx)}
                                            type="button"
                                            className="btn-icon remove-item-btn" >
                                            <i className="la la-minus-circle" />
                                        </button> : null
                                        }
                                        {itm.type}
                                    </td>
                                    <td style={{ width: '10%' }}> {itm.subtype} </td>
                                    <td style={{ width: '4%' }}> {itm.unitOfMeasure} </td>
                                    <td style={{ width: '20%' }}> {itm.description} </td>
                                    <td style={{ width: '8%' }}> {itm.partNumber} </td>
                                    <td style={{ width: '8%' }}>
                                        <input
                                            id="quantity1"
                                            className="form-control"
                                            name="quantity1"
                                            value={itm.quantity1}
                                            min="0"
                                            disabled={disableEdit()}
                                            onChange={(e) => handleChange(e, idx)}
                                            onBlur={(e) => handleBlur(e, idx)}
                                        />
                                    </td>
                                    <td style={{ width: '8%' }}>
                                        <input
                                            id="quantity2"
                                            className="form-control"
                                            name="quantity2"
                                            value={itm.quantity2}
                                            min="0"
                                            disabled={disableEdit()}
                                            onChange={(e) => handleChange(e, idx)}
                                            onBlur={(e) => handleBlur(e, idx)}
                                        />
                                    </td>
                                    <td style={{ width: '8%' }}>
                                        <input
                                            id="quantity3"
                                            className="form-control"
                                            name="quantity3"
                                            value={itm.quantity3}
                                            min="0"
                                            disabled={disableEdit()}
                                            onChange={(e) => handleChange(e, idx)}
                                            onBlur={(e) => handleBlur(e, idx)}
                                        />
                                    </td>
                                    <td style={{ width: '8%' }}>
                                        <input
                                            id="quantity4"
                                            className="form-control"
                                            name="quantity4"
                                            value={itm.quantity4}
                                            min="0"
                                            disabled={disableEdit()}
                                            onChange={(e) => handleChange(e, idx)}
                                            onBlur={(e) => handleBlur(e, idx)}
                                        />
                                    </td>
                                    <td style={{ width: '8%' }}>
                                        <input
                                            id="quantity5"
                                            className="form-control"
                                            name="quantity5"
                                            value={itm.quantity5}
                                            min="0"
                                            disabled={disableEdit()}
                                            onChange={(e) => handleChange(e, idx)}
                                            onBlur={(e) => handleBlur(e, idx)}
                                        />
                                    </td>
                                    <td style={{ width: '8%' }}>
                                        <input
                                            id="quantity6"
                                            className="form-control"
                                            name="quantity6"
                                            value={itm.quantity6}
                                            min="0"
                                            disabled={disableEdit()}
                                            onChange={(e) => handleChange(e, idx)}
                                            onBlur={(e) => handleBlur(e, idx)}
                                        />
                                    </td>
                                </tr>
                            })
                        }
                        <tr>
                            <td colSpan={2}>
                                <button
                                    id="add-item"
                                    onClick={addItem}
                                    type="button"
                                    disabled={entry.status == PhysicalControlGroupStatus.complete}
                                    className="btn-icon add-item-btn" >
                                    <i className="la la-plus-circle" />
                                    Add Item
                                </button>
                            </td>
                            <td colSpan={2}>
                                <AutoComplete
                                    id="part-autocomplete"
                                    dispatch={dispatch}
                                    onSelectedCallback={ onPartSelected }
                                    placeholder="Part #"
                                    currentValue={newPart ? newPart.partNumber : ''}
                                    suggestionRender={'partNumber'}
                                    disabled={entry.status == PhysicalControlGroupStatus.complete}
                                    endpoint={ApiEndpoints.INVENTORY_PARTS_AUTOCOMPLETE.formattify(getReportId())}
                                /> 
                            </td>
                        </tr>
                    </Table>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    {getBottomButtons()}
                </div>
            </div>
        </PageBody>
    </PageContent >
});